import classNames from "classnames";
import { Flex } from "./Flex";
import "./Icon.css";
import type { OfferingCategory } from "@albertine/types";

/* Symbol name from: https://fonts.google.com/icons */
type MaterialDesignIconName =
    | "add_link"
    | "add_photo_alternate"
    | "add"
    | "arrow_back"
    | "arrow_downward"
    | "arrow_forward"
    | "arrow_upward"
    | "assignment_add"
    | "attach_file_add"
    | "attach_file"
    | "autorenew"
    | "calendar_clock"
    | "calendar_month"
    | "calendar_today"
    | "call"
    | "chat_paste_go"
    | "cancel"
    | "check"
    | "check_circle"
    | "chevron_left"
    | "chevron_right"
    | "close"
    | "commute"
    | "concierge"
    | "content_copy"
    | "content_paste"
    | "crop"
    | "delete"
    | "description"
    | "drag_indicator"
    | "edit"
    | "error"
    | "event"
    | "event_available"
    | "expand_less"
    | "expand_more"
    | "favorite"
    | "filter_list"
    | "flight"
    | "fullscreen"
    | "handshake"
    | "hotel"
    | "import_contacts"
    | "keyboard_tab"
    | "label"
    | "link"
    | "list"
    | "local_activity"
    | "local_bar"
    | "location_on"
    | "lock"
    | "logout"
    | "mark_unread_chat_alt"
    | "mic"
    | "more_horiz"
    | "more_vert"
    | "notes"
    | "notification_add"
    | "notifications"
    | "open_in_new"
    | "person_add"
    | "photo_camera"
    | "photo_frame"
    | "picture_as_pdf"
    | "priority_high"
    | "problem"
    | "question_mark"
    | "refresh"
    | "reply"
    | "restaurant_menu"
    | "restore_from_trash"
    | "rotate_right"
    | "route"
    | "schedule"
    | "search"
    | "send"
    | "sensors"
    | "settings"
    | "shopping_bag"
    | "spa"
    | "star_rate"
    | "thumb_down"
    | "thumb_up"
    | "train"
    | "undo"
    | "unfold_less"
    | "unfold_more"
    | "unpublished"
    | "vpn_key"
    | "info"
    | "attach_money"
    | "credit_card"
    | "wand_stars"
    | "map";

type BaseIcon = (className?: string) => (props: Props & IconProps) => React.JSX.Element;

type Props = {
    className?: string;
};

type IconProps = {
    style?: React.CSSProperties;
};

function BaseMaterialIcon(name: MaterialDesignIconName) {
    return function BaseIcon(baseClassName?: string) {
        return function Icon(props: Props & IconProps) {
            const { className, style } = props;

            return (
                <span
                    className={classNames(
                        "icon",
                        "material-symbols-outlined",
                        baseClassName,
                        className,
                    )}
                    style={style}
                >
                    {name}
                </span>
            );
        };
    };
}

function BasePictogramIcon(name: MaterialDesignIconName) {
    return function BaseIcon(baseClassName?: string) {
        return function Icon(props: Props) {
            const { className } = props;

            return (
                <Flex.Row
                    justifyContent="center"
                    className={classNames(
                        "pictogram-icon__container",
                        baseClassName,
                        className,
                    )}
                >
                    <span
                        className={classNames(
                            "pictogram-icon",
                            "material-symbols-outlined",
                        )}
                    >
                        {name}
                    </span>
                </Flex.Row>
            );
        };
    };
}

function Add(baseIcon: BaseIcon, className: string) {
    return (newClassName?: string) => baseIcon(classNames(className, newClassName));
}

const Fill = (baseComponent: BaseIcon) =>
    Object.assign(baseComponent(), {
        Fill: baseComponent("icon--fill"),
    });

const MdColors = (baseComponent: BaseIcon) =>
    Object.assign(baseComponent(), {
        N10: Fill(Add(baseComponent, "icon--n10")),
        N20: Fill(Add(baseComponent, "icon--n20")),
        N50: Fill(Add(baseComponent, "icon--n50")),
        N60: Fill(Add(baseComponent, "icon--n60")),
        N70: Fill(Add(baseComponent, "icon--n70")),
        N80: Fill(Add(baseComponent, "icon--n80")),
        N90: Fill(Add(baseComponent, "icon--n90")),
        N99: Fill(Add(baseComponent, "icon--n99")),
        N100: Fill(Add(baseComponent, "icon--n100")),
        P10: Fill(Add(baseComponent, "icon--p10")),
        P30: Fill(Add(baseComponent, "icon--p30")),
        P50: Fill(Add(baseComponent, "icon--p50")),
        P60: Fill(Add(baseComponent, "icon--p60")),
        P80: Fill(Add(baseComponent, "icon--p80")),
        P90: Fill(Add(baseComponent, "icon--p90")),
        P95: Fill(Add(baseComponent, "icon--p95")),
        S10: Fill(Add(baseComponent, "icon--s10")),
        S30: Fill(Add(baseComponent, "icon--s30")),
        S50: Fill(Add(baseComponent, "icon--s50")),
        S80: Fill(Add(baseComponent, "icon--s80")),
        S90: Fill(Add(baseComponent, "icon--s90")),
        S95: Fill(Add(baseComponent, "icon--s95")),
        T10: Fill(Add(baseComponent, "icon--t10")),
        T30: Fill(Add(baseComponent, "icon--t30")),
        T50: Fill(Add(baseComponent, "icon--t50")),
        T80: Fill(Add(baseComponent, "icon--t80")),
        T90: Fill(Add(baseComponent, "icon--t90")),
        T95: Fill(Add(baseComponent, "icon--t95")),
        E10: Fill(Add(baseComponent, "icon--e10")),
        E40: Fill(Add(baseComponent, "icon--e40")),
        E50: Fill(Add(baseComponent, "icon--e50")),
        E60: Fill(Add(baseComponent, "icon--e60")),
        E70: Fill(Add(baseComponent, "icon--e70")),
        E90: Fill(Add(baseComponent, "icon--e90")),
    });

const PictogramColors = (baseComponent: BaseIcon) =>
    Object.assign(baseComponent(), {
        E40: baseComponent("icon--e40"),
        E90: baseComponent("icon--e90"),
        N90: baseComponent("icon--n90"),
        N99: baseComponent("icon--n99"),
        P90: baseComponent("icon--p90"),
        S90: baseComponent("icon--s90"),
        T90: baseComponent("icon--t90"),
    });

const MdSizes = (baseComponent: BaseIcon) =>
    Object.assign(baseComponent(), {
        Large: MdColors(Add(baseComponent, "icon--large")),
        Medium: MdColors(Add(baseComponent, "icon--medium")),
        Small: MdColors(Add(baseComponent, "icon--small")),
        XSmall: MdColors(Add(baseComponent, "icon--xsmall")),
    });

const PictogramSizes = (baseComponent: BaseIcon) =>
    Object.assign(baseComponent(), {
        Large: MdColors(Add(baseComponent, "icon--large")),
        Medium: PictogramColors(Add(baseComponent, "icon--medium")),
        Small: PictogramColors(Add(baseComponent, "icon--small")),
    });

const MaterialIcon = (name: MaterialDesignIconName) =>
    Object.assign(BaseMaterialIcon(name), MdSizes(BaseMaterialIcon(name)));

const PictogramIconHelper = (name: MaterialDesignIconName) =>
    Object.assign(BasePictogramIcon(name), PictogramSizes(BasePictogramIcon(name)));

type IconComponent = BaseIcon & {
    Large: ReturnType<typeof MdColors>;
    Medium: ReturnType<typeof MdColors>;
    Small: ReturnType<typeof MdColors>;
    XSmall: ReturnType<typeof MdColors>;
};

const Icon: Record<string, IconComponent> = {
    Add: MaterialIcon("add"),
    AddLink: MaterialIcon("add_link"),
    AddPhotoAlternate: MaterialIcon("add_photo_alternate"),
    ArrowBack: MaterialIcon("arrow_back"),
    ArrowDownward: MaterialIcon("arrow_downward"),
    ArrowForward: MaterialIcon("arrow_forward"),
    ArrowUpward: MaterialIcon("arrow_upward"),
    AssignmentAdd: MaterialIcon("assignment_add"),
    AttachFile: MaterialIcon("attach_file"),
    AttachFileAdd: MaterialIcon("attach_file_add"),
    AutoRenew: MaterialIcon("autorenew"),
    CalendarClock: MaterialIcon("calendar_clock"),
    CalendarMonth: MaterialIcon("calendar_month"),
    CalendarToday: MaterialIcon("calendar_today"),
    Call: MaterialIcon("call"),
    ChatPasteGo: MaterialIcon("chat_paste_go"),
    Cancel: MaterialIcon("cancel"),
    Check: MaterialIcon("check"),
    CheckCircle: MaterialIcon("check_circle"),
    ChevronLeft: MaterialIcon("chevron_left"),
    ChevronRight: MaterialIcon("chevron_right"),
    Close: MaterialIcon("close"),
    Commute: MaterialIcon("commute"),
    Concierge: MaterialIcon("concierge"),
    ContentCopy: MaterialIcon("content_copy"),
    ContentPaste: MaterialIcon("content_paste"),
    Crop: MaterialIcon("crop"),
    Delete: MaterialIcon("delete"),
    Description: MaterialIcon("description"),
    DragIndicator: MaterialIcon("drag_indicator"),
    Edit: MaterialIcon("edit"),
    Error: MaterialIcon("error"),
    Event: MaterialIcon("event"),
    EventAvailable: MaterialIcon("event_available"),
    ExpandLess: MaterialIcon("expand_less"),
    ExpandMore: MaterialIcon("expand_more"),
    Favorite: MaterialIcon("favorite"),
    FilterList: MaterialIcon("filter_list"),
    Flight: MaterialIcon("flight"),
    Fullscreen: MaterialIcon("fullscreen"),
    Handshake: MaterialIcon("handshake"),
    Hotel: MaterialIcon("hotel"),
    ImportContacts: MaterialIcon("import_contacts"),
    KeyBoardTab: MaterialIcon("keyboard_tab"),
    Label: MaterialIcon("label"),
    Link: MaterialIcon("link"),
    List: MaterialIcon("list"),
    LocalActivity: MaterialIcon("local_activity"),
    LocalBar: MaterialIcon("local_bar"),
    LocationOn: MaterialIcon("location_on"),
    Lock: MaterialIcon("lock"),
    Logout: MaterialIcon("logout"),
    Map: MaterialIcon("map"),
    MarkUnreadChatAlt: MaterialIcon("mark_unread_chat_alt"),
    Mic: MaterialIcon("mic"),
    MoreHoriz: MaterialIcon("more_horiz"),
    MoreVert: MaterialIcon("more_vert"),
    Notes: MaterialIcon("notes"),
    NotificationAdd: MaterialIcon("notification_add"),
    Notifications: MaterialIcon("notifications"),
    OpenInNew: MaterialIcon("open_in_new"),
    PersonAdd: MaterialIcon("person_add"),
    PhotoCamera: MaterialIcon("photo_camera"),
    PhotoFrame: MaterialIcon("photo_frame"),
    PictureAsPdf: MaterialIcon("picture_as_pdf"),
    PriorityHigh: MaterialIcon("priority_high"),
    Problem: MaterialIcon("problem"),
    QuestionMark: MaterialIcon("question_mark"),
    Refresh: MaterialIcon("refresh"),
    Reply: MaterialIcon("reply"),
    RestaurantMenu: MaterialIcon("restaurant_menu"),
    RestoreFromTrash: MaterialIcon("restore_from_trash"),
    RotateRight: MaterialIcon("rotate_right"),
    Route: MaterialIcon("route"),
    Schedule: MaterialIcon("schedule"),
    Search: MaterialIcon("search"),
    Send: MaterialIcon("send"),
    Sensors: MaterialIcon("sensors"),
    Settings: MaterialIcon("settings"),
    ShoppingBag: MaterialIcon("shopping_bag"),
    Spa: MaterialIcon("spa"),
    StarRate: MaterialIcon("star_rate"),
    ThumbDown: MaterialIcon("thumb_down"),
    ThumbUp: MaterialIcon("thumb_up"),
    Train: MaterialIcon("train"),
    Undo: MaterialIcon("undo"),
    UnfoldLess: MaterialIcon("unfold_less"),
    UnfoldMore: MaterialIcon("unfold_more"),
    Unpublished: MaterialIcon("unpublished"),
    VpnKey: MaterialIcon("vpn_key"),
    WandStars: MaterialIcon("wand_stars"),
    Info: MaterialIcon("info"),
    AttachMoney: MaterialIcon("attach_money"),
    CreditCard: MaterialIcon("credit_card"),
};

export default Icon as Record<keyof typeof Icon, IconComponent>;

export const PictogramIcon = {
    Add: PictogramIconHelper("add"),
    AddLink: PictogramIconHelper("add_link"),
    AddPhotoAlternate: PictogramIconHelper("add_photo_alternate"),
    ArrowBack: PictogramIconHelper("arrow_back"),
    ArrowDownward: PictogramIconHelper("arrow_downward"),
    ArrowForward: PictogramIconHelper("arrow_forward"),
    ArrowUpward: PictogramIconHelper("arrow_upward"),
    AssignmentAdd: PictogramIconHelper("assignment_add"),
    AttachFile: PictogramIconHelper("attach_file"),
    AttachFileAdd: PictogramIconHelper("attach_file_add"),
    AutoRenew: PictogramIconHelper("autorenew"),
    CalendarClock: PictogramIconHelper("calendar_clock"),
    CalendarMonth: PictogramIconHelper("calendar_month"),
    CalendarToday: PictogramIconHelper("calendar_today"),
    Call: PictogramIconHelper("call"),
    ChatPasteGo: PictogramIconHelper("chat_paste_go"),
    Cancel: PictogramIconHelper("cancel"),
    Check: PictogramIconHelper("check"),
    CheckCircle: PictogramIconHelper("check_circle"),
    ChevronLeft: PictogramIconHelper("chevron_left"),
    ChevronRight: PictogramIconHelper("chevron_right"),
    Close: PictogramIconHelper("close"),
    Commute: PictogramIconHelper("commute"),
    Concierge: PictogramIconHelper("concierge"),
    ContentCopy: PictogramIconHelper("content_copy"),
    ContentPaste: PictogramIconHelper("content_paste"),
    Crop: PictogramIconHelper("crop"),
    Delete: PictogramIconHelper("delete"),
    Description: PictogramIconHelper("description"),
    DragIndicator: PictogramIconHelper("drag_indicator"),
    Edit: PictogramIconHelper("edit"),
    Error: PictogramIconHelper("error"),
    Event: PictogramIconHelper("event"),
    EventAvailable: PictogramIconHelper("event_available"),
    ExpandLess: PictogramIconHelper("expand_less"),
    ExpandMore: PictogramIconHelper("expand_more"),
    Favorite: PictogramIconHelper("favorite"),
    FilterList: PictogramIconHelper("filter_list"),
    Flight: PictogramIconHelper("flight"),
    Handshake: PictogramIconHelper("handshake"),
    Hotel: PictogramIconHelper("hotel"),
    ImportContacts: PictogramIconHelper("import_contacts"),
    KeyBoardTab: PictogramIconHelper("keyboard_tab"),
    Label: PictogramIconHelper("label"),
    Link: PictogramIconHelper("link"),
    List: PictogramIconHelper("list"),
    LocalActivity: PictogramIconHelper("local_activity"),
    LocalBar: PictogramIconHelper("local_bar"),
    LocationOn: PictogramIconHelper("location_on"),
    Lock: PictogramIconHelper("lock"),
    Logout: PictogramIconHelper("logout"),
    MarkUnreadChatAlt: PictogramIconHelper("mark_unread_chat_alt"),
    Mic: PictogramIconHelper("mic"),
    MoreHoriz: PictogramIconHelper("more_horiz"),
    MoreVert: PictogramIconHelper("more_vert"),
    Notes: PictogramIconHelper("notes"),
    NotificationAdd: PictogramIconHelper("notification_add"),
    Notifications: PictogramIconHelper("notifications"),
    OpenInNew: PictogramIconHelper("open_in_new"),
    PersonAdd: PictogramIconHelper("person_add"),
    PhotoCamera: PictogramIconHelper("photo_camera"),
    PhotoFrame: PictogramIconHelper("photo_frame"),
    PictureAsPdf: PictogramIconHelper("picture_as_pdf"),
    PriorityHigh: PictogramIconHelper("priority_high"),
    Problem: PictogramIconHelper("problem"),
    QuestionMark: PictogramIconHelper("question_mark"),
    Refresh: PictogramIconHelper("refresh"),
    Reply: PictogramIconHelper("reply"),
    RestaurantMenu: PictogramIconHelper("restaurant_menu"),
    RestoreFromTrash: PictogramIconHelper("restore_from_trash"),
    RotateRight: PictogramIconHelper("rotate_right"),
    Route: PictogramIconHelper("route"),
    Schedule: PictogramIconHelper("schedule"),
    Search: PictogramIconHelper("search"),
    Send: PictogramIconHelper("send"),
    Sensors: PictogramIconHelper("sensors"),
    Settings: PictogramIconHelper("settings"),
    ShoppingBag: PictogramIconHelper("shopping_bag"),
    Spa: PictogramIconHelper("spa"),
    StarRate: PictogramIconHelper("star_rate"),
    ThumbDown: PictogramIconHelper("thumb_down"),
    ThumbUp: PictogramIconHelper("thumb_up"),
    Train: PictogramIconHelper("train"),
    Undo: PictogramIconHelper("undo"),
    UnfoldLess: PictogramIconHelper("unfold_less"),
    UnfoldMore: PictogramIconHelper("unfold_more"),
    Unpublished: PictogramIconHelper("unpublished"),
    VpnKey: PictogramIconHelper("vpn_key"),
    WandStars: PictogramIconHelper("wand_stars"),
    Info: PictogramIconHelper("info"),
};

export type MdIconName = keyof typeof Icon;
export type PictogramIcon = typeof PictogramIcon;

export const CategoryIconMap: Record<OfferingCategory, keyof typeof PictogramIcon> = {
    accommodation: "Hotel",
    dining: "RestaurantMenu",
    event: "LocalActivity",
    localExperience: "LocalActivity",
    flight: "Flight",
    nightlife: "LocalBar",
    services: "Handshake",
    shopping: "ShoppingBag",
    train: "Train",
    transportation: "Commute",
    travelItinerary: "Route",
    wellness: "Spa",
    other: "MoreHoriz",
};
